// File: app/src/hooks/useBodyPointerEventsGuard.js
//
// App-wide self-healing guard for the Radix "stuck pointer-events" hang
// (#72, recurrence of #52). Mount once, at the root.
//
// Rather than patching each destructive call site with its own restore — the
// approach that let #52 come back through a different button — this watches
// <body> for the inline `pointer-events: none` lock and releases it whenever no
// Radix layer is actually open to justify it. Call sites stay free to defer
// their state swap by a frame (still the correct ordering); this is the net
// underneath them.
//
// The check is deliberately late and double-confirmed: a layer sets the lock
// as it opens, so an immediate verdict would race the mount and could unlock a
// dialog that is legitimately opening. We wait two frames, then re-confirm
// after a short delay before touching anything.

import { useEffect } from "react";
import {
  isPointerEventsLocked,
  isPointerEventsStuck,
  releaseStuckPointerEvents,
} from "../utils/pointerEventsGuard";

// How long after the lock appears we re-confirm before releasing it. Long
// enough for any Radix open/close animation to settle, short enough that a
// user who taps a dead control gets it back before trying a refresh.
const CONFIRM_DELAY_MS = 400;

export function useBodyPointerEventsGuard() {
  useEffect(() => {
    if (typeof MutationObserver === "undefined" || !document?.body) return;

    let rafId = null;
    let timerId = null;

    const cancelPending = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timerId !== null) clearTimeout(timerId);
      rafId = null;
      timerId = null;
    };

    // Second pass: only release if the lock is *still* unbacked by an open
    // layer. A dialog that has finished mounting in the meantime keeps its lock.
    const confirmAndRelease = () => {
      timerId = null;
      if (releaseStuckPointerEvents(document)) {
        console.warn(
          "[PointerGuard] Released a stuck body pointer-events lock (#72)",
        );
      }
    };

    // First pass: two frames after the mutation, so a layer that is mid-mount
    // has had a chance to render its content node.
    const scheduleCheck = () => {
      if (rafId !== null || timerId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (!isPointerEventsStuck(document)) return;
          timerId = setTimeout(confirmAndRelease, CONFIRM_DELAY_MS);
        });
      });
    };

    const observer = new MutationObserver(() => {
      // Clearing the style re-fires the observer; the guard no-ops then.
      if (isPointerEventsLocked(document)) scheduleCheck();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // A lock can already be in place when this mounts (a remount after the
    // damage was done), so evaluate once up front too.
    if (isPointerEventsLocked(document)) scheduleCheck();

    return () => {
      observer.disconnect();
      cancelPending();
    };
  }, []);
}

export default useBodyPointerEventsGuard;
